import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { countryOptions, flagEmoji, formatPhone, loadPhoneLib, type PhoneLib } from '../lib/phone';

interface Props {
  base: string;
  value: string;
  country: string;
  required: boolean;
  error?: string;
  /** Called with the formatted text and the country it belongs to. */
  onChange: (value: string, country: string) => void;
  onBlur: (lib: PhoneLib | null) => void;
  /** Hands the loaded library (or null) to the form for validation at submit. */
  onLib: (lib: PhoneLib | null) => void;
}

/** Phone input with a country picker, a flag that follows the number, and as-you-type formatting. */
export function PhoneField(p: Props) {
  const [lib, setLib] = useState<PhoneLib | null>(null);
  const last = useRef(p.value);

  useEffect(() => {
    let alive = true;
    void loadPhoneLib(p.base).then((l) => {
      if (!alive) return;
      setLib(l);
      p.onLib(l);
      if (l) {
        // Format anything already typed or autofilled before the rules arrived. The country stays
        // on the form's default (US) rather than following the browser language, which misfires.
        const f = formatPhone(l, p.value, p.country);
        last.current = f.text;
        p.onChange(f.text, f.country);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  const options = useMemo(() => {
    if (!lib) return [];
    let nameOf = (code: string) => code;
    try {
      const names = new Intl.DisplayNames(['en'], { type: 'region' });
      nameOf = (code) => names.of(code) ?? code;
    } catch {}
    return countryOptions(lib, nameOf);
  }, [lib]);
  const dial = lib ? `+${lib.getCountryCallingCode(p.country)}` : '';

  const onInput = (text: string, inputType?: string) => {
    // While deleting, leave the text alone: re-adding the bracket or space the person just
    // removed would make backspace feel stuck. It is tidied again on blur.
    const deleting = inputType
      ? inputType.startsWith('delete')
      : last.current.startsWith(text) && text.length < last.current.length;
    const next = lib && !deleting ? formatPhone(lib, text, p.country) : null;
    const value = next ? next.text : text;
    last.current = value;
    p.onChange(value, next ? next.country : p.country);
  };

  return (
    <div class="field">
      <label class="label" for="phone">
        Phone
        {p.required ? null : <span class="req"> (optional)</span>}
      </label>
      <div class="phone">
        {lib ? (
          <div class="phone-cc">
            <span class="phone-flag" aria-hidden="true">
              {flagEmoji(p.country)}
            </span>
            <span class="phone-dial" aria-hidden="true">
              {dial}
            </span>
            <select
              class="phone-select"
              aria-label="Country"
              value={p.country}
              onChange={(e) => {
                const country = (e.currentTarget as HTMLSelectElement).value;
                // An international entry belongs to its own country: start the number again.
                const text = p.value.trim().startsWith('+') ? '' : p.value;
                const f = formatPhone(lib, text, country);
                last.current = f.text;
                p.onChange(f.text, country);
              }}
            >
              {options.map((c) => (
                <option key={c.code} value={c.code}>
                  {`${c.name} (${c.dial})`}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <input
          class="input"
          id="phone"
          name="phone"
          type="tel"
          inputmode="tel"
          autocomplete="tel"
          value={p.value}
          required={p.required}
          aria-required={p.required ? 'true' : undefined}
          aria-invalid={p.error ? 'true' : undefined}
          aria-describedby={p.error ? 'phone-err' : undefined}
          onInput={(e) =>
            onInput((e.currentTarget as HTMLInputElement).value, (e as InputEvent).inputType)
          }
          onBlur={() => {
            if (lib && p.value) {
              const f = formatPhone(lib, p.value, p.country);
              last.current = f.text;
              if (f.text !== p.value || f.country !== p.country) p.onChange(f.text, f.country);
            }
            p.onBlur(lib);
          }}
        />
      </div>
      {p.error ? (
        <span class="err" id="phone-err">
          {p.error}
        </span>
      ) : null}
    </div>
  );
}
